import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

const API_URL = process.env.REACT_APP_API_URL;
const VarauskalenteritRaportointiContext = createContext();

/* ==========================================================
   Provider
========================================================== */
export const VarauskalenteritRaportointiProvider = ({
  children,
  talonyhtioId,
  tilanTyyppi,
}) => {
  const [raportointiData, setRaportointiData] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔐 ETag pidetään muistissa (per taloyhtiö)
  const etagRef = useRef(null);

  /* ==========================================================
     FETCH (ETag-aware)
  ========================================================== */
  const fetchData = useCallback(async () => {
    if (!talonyhtioId || !tilanTyyppi) return;

    setLoading(true);

    try {
      const headers = {};
      if (etagRef.current) {
        headers["If-None-Match"] = etagRef.current;
      }

      const res = await fetch(
        `${API_URL}/getAggregatedReservationData?talonyhtioId=${encodeURIComponent(
          talonyhtioId
        )}&tilanTyyppi=${encodeURIComponent(tilanTyyppi)}`,
        { headers }
      );

      // 🔁 Data ei muuttunut
      if (res.status === 304) {
        setLoading(false);
        return;
      }

      if (!res.ok) throw new Error("fetch_failed");

      const data = await res.json();
      const newEtag = res.headers.get("ETag");

      etagRef.current = newEtag;
      setRaportointiData(data);
    } catch (e) {
      console.error("Error fetching aggregated reservation data:", e);
    } finally {
      setLoading(false);
    }
  }, [talonyhtioId, tilanTyyppi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ==========================================================
     SET INVOICE STATUS
     - optimistic UI
     - backend bumpaa ETagin
     - refetch synkkaa lopullisen totuuden
  ========================================================== */
  const setInvoiceStatus = useCallback(
    async ({ reportId, käyttäjäid, laskutettu, actorUserId }) => {
      if (!reportId || !käyttäjäid) return;

      // 🟡 Optimistic UI
      setRaportointiData((prev) =>
        prev.map((r) =>
          r.id !== reportId
            ? r
            : {
                ...r,
                users: (r.users || []).map((u) =>
                  u.käyttäjäid !== käyttäjäid
                    ? u
                    : {
                        ...u,
                        laskutettu,
                        laskutettuAt: laskutettu
                          ? new Date().toISOString()
                          : null,
                        laskutettuBy: laskutettu
                          ? actorUserId || null
                          : null,
                      }
                ),
              }
        )
      );

      try {
        const res = await fetch(`${API_URL}/setInvoiceStatus`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reportId,
            käyttäjäid,
            laskutettu,
            actorUserId,
          }),
        });

        if (!res.ok) throw new Error("set_invoice_failed");

        // 🔄 backend bumpaa versionin → uusi ETag → haetaan tuore data
        await fetchData();
      } catch (e) {
        console.error("setInvoiceStatus failed:", e);
        // 🔁 fallback: hae backendin totuus
        await fetchData();
      }
    },
    [fetchData]
  );

  /* ==========================================================
     CONTEXT VALUE
  ========================================================== */
  return (
    <VarauskalenteritRaportointiContext.Provider
      value={{
        raportointiData,
        loading,
        refetch: fetchData,
        setInvoiceStatus,
      }}
    >
      {children}
    </VarauskalenteritRaportointiContext.Provider>
  );
};

/* ==========================================================
   Hook
========================================================== */
export const useVarauskalenteritRaportointi = () =>
  useContext(VarauskalenteritRaportointiContext);
